import React from "react";
import {
  Card,
  Text,
  Stack,
  Select,
  Checkbox,
  SimpleGrid,
  Paper,
  Group,
  NumberInput,
  Button,
} from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { WEIGHT_UNIT_OPTIONS } from "./index";

const BulkEditForm = ({
  categoryLvl1,
  categoryLvl2,
  categoryLvl3,
  selCatLvl1,
  setSelCatLvl1,
  selCatLvl2,
  setSelCatLvl2,
  selCatLvl3,
  setSelCatLvl3,
  loadCategoryLvl2,
  loadCategoryLvl3,
  loadingRefs,
  selTags,
  setSelTags,
  tagList,
  weight,
  setWeight,
  weightUnit,
  setWeightUnit,
  countryList,
  countryId,
  setCountryId,
  warehouseList,
  selWarehouse,
  setSelWarehouse,
  sellerStatus,
  setSellerStatus,
  visibilityValue,
  setVisibilityValue,
  saving,
  productsLength,
  isLoading,
  handleSave,
}) => {
  return (
    <Stack gap="md">
      {/* Category */}
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} size="sm" mb={12}>
          Category
        </Text>
        <Text size="xs" c="dimmed" mb={10}>
          Leave blank to keep each product's existing category.
        </Text>
        <Stack gap={8}>
          <Select
            label="Level 1"
            placeholder="Select main category"
            data={categoryLvl1}
            value={selCatLvl1}
            searchable
            clearable
            onChange={(val) => {
              setSelCatLvl1(val);
              loadCategoryLvl2(val);
            }}
            disabled={loadingRefs}
          />
          <Select
            label="Level 2"
            placeholder={
              selCatLvl1
                ? "Select sub-category"
                : "Select Level 1 first"
            }
            data={categoryLvl2}
            value={selCatLvl2}
            searchable
            clearable
            disabled={!selCatLvl1 || loadingRefs}
            onChange={(val) => {
              setSelCatLvl2(val);
              loadCategoryLvl3(val);
            }}
          />
          <Select
            label="Level 3"
            placeholder={
              selCatLvl2
                ? "Select sub-sub-category"
                : "Select Level 2 first"
            }
            data={categoryLvl3}
            value={selCatLvl3}
            searchable
            clearable
            disabled={!selCatLvl2 || loadingRefs}
            onChange={setSelCatLvl3}
          />
        </Stack>
      </Card>

      {/* Tags / Labels */}
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} size="sm" mb={4}>
          Labels / Tags
        </Text>
        <Text size="xs" c="dimmed" mb={12}>
          Leave blank to keep existing tags on each product.
        </Text>
        <Checkbox.Group value={selTags} onChange={setSelTags}>
          <SimpleGrid cols={2} spacing="xs">
            {tagList.map((tag) => (
              <Paper key={tag.value} p="xs" withBorder radius="sm">
                <Checkbox
                  value={tag.value}
                  label={
                    <Text size="xs" fw={600}>
                      {tag.label}
                    </Text>
                  }
                  description={
                    <Text size="10px" c="dimmed" lineClamp={2}>
                      {tag.caption}
                    </Text>
                  }
                />
              </Paper>
            ))}
          </SimpleGrid>
        </Checkbox.Group>
      </Card>

      {/* Weight */}
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} size="sm" mb={12}>
          Weight
        </Text>
        <Group grow>
          <NumberInput
            label="Weight Value"
            placeholder="Leave blank to skip"
            value={weight}
            onChange={setWeight}
            min={0}
            decimalScale={2}
          />
          <Select
            label="Unit"
            data={WEIGHT_UNIT_OPTIONS}
            value={weightUnit}
            onChange={setWeightUnit}
            allowDeselect={false}
          />
        </Group>
      </Card>

      {/* Country */}
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} size="sm" mb={12}>
          Country of Origin
        </Text>
        <Select
          label="Country"
          placeholder="Leave blank to keep existing"
          data={countryList}
          value={countryId}
          onChange={setCountryId}
          searchable
          clearable
          disabled={loadingRefs}
        />
      </Card>

      {/* Warehouse */}
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} size="sm" mb={12}>
          Warehouse
        </Text>
        <Select
          label="Warehouse"
          placeholder="Leave blank to keep existing"
          data={warehouseList}
          value={selWarehouse}
          onChange={setSelWarehouse}
          searchable
          clearable
          disabled={loadingRefs}
        />
      </Card>

      {/* Product Seller Status & Visibility */}
      <Card withBorder radius="md" padding="lg">
        <Text fw={600} size="sm" mb={12}>
          Product Status & Visibility
        </Text>
        <Stack gap={8}>
          <Select
            label="Product Seller Status"
            placeholder="Leave blank to keep existing"
            data={[
              { value: 'pass', label: 'Pass' },
              { value: 'in review', label: 'In Review' },
              { value: 'rejecter', label: 'Rejecter' }
            ]}
            value={sellerStatus}
            onChange={(val) => {
              setSellerStatus(val);
              if (val !== 'pass' && visibilityValue === 'live') {
                setVisibilityValue(null);
              }
            }}
            clearable
          />
          <Select
            label="Visibility"
            placeholder="Leave blank to keep existing"
            data={[
              { value: 'live', label: 'Live', disabled: sellerStatus !== 'pass' },
              { value: 'hidden', label: 'Hidden from Catalog' }
            ]}
            value={visibilityValue}
            onChange={setVisibilityValue}
            clearable
          />
        </Stack>
      </Card>

      {/* Save button */}
      <Button
        fullWidth
        size="md"
        leftSection={<IconDeviceFloppy size={18} />}
        loading={saving}
        disabled={productsLength === 0 || isLoading}
        onClick={handleSave}
      >
        Save Changes to {productsLength} Product
        {productsLength !== 1 ? "s" : ""}
      </Button>
    </Stack>
  );
};

export default BulkEditForm;
